import { collection, getDocs, query, where, type Firestore } from 'firebase/firestore';
import { FIRESTORE_PURCHASE_ORDERS_COLLECTION } from '../constants/firestoreCollectionSecurity';
import { isSidebarOpsPoIndependentType } from '../constants/sidebarOpsPoHygieneExemptions';
import { isPurchaseOrderActiveForActionRequired } from './notificationFirestoreQueries';

const FIRESTORE_IN_QUERY_MAX = 30;

export type SidebarPartyField = 'shipperId' | 'brokerId';

/**
 * Party-scoped batch lookup: is each PO still actionable for sidebar dots?
 * Missing PO docs are treated as inactive (terminal / deleted).
 */
export async function batchResolvePoActiveForSidebar(
  db: Firestore,
  partyId: string,
  poNumbers: string[],
  partyField: SidebarPartyField
): Promise<Map<string, boolean>> {
  const result = new Map<string, boolean>();
  const unique = [...new Set(poNumbers.map((p) => p.trim()).filter(Boolean))];
  if (!partyId || unique.length === 0) return result;

  for (let i = 0; i < unique.length; i += FIRESTORE_IN_QUERY_MAX) {
    const chunk = unique.slice(i, i + FIRESTORE_IN_QUERY_MAX);
    const snap = await getDocs(
      query(
        collection(db, FIRESTORE_PURCHASE_ORDERS_COLLECTION),
        where(partyField, '==', partyId),
        where('poNumber', 'in', chunk)
      )
    );

    const resolvedInChunk = new Set<string>();
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const poNumber = String(data.poNumber || '').trim();
      if (!poNumber) return;
      resolvedInChunk.add(poNumber);
      result.set(
        poNumber,
        isPurchaseOrderActiveForActionRequired(data as Record<string, unknown>)
      );
    });

    chunk.forEach((poNumber) => {
      if (!resolvedInChunk.has(poNumber)) {
        result.set(poNumber, false);
      }
    });
  }

  return result;
}

export interface SidebarOpsNotificationLike {
  poNumber?: string;
  type?: string;
}

/**
 * Sidebar dots only for ops tied to live POs. Notifications without a PO stay eligible.
 * While PO status is unresolved, fail-closed (no dot) to avoid zombie resurfacing.
 */
export function isSidebarOpsNotificationActionable(
  notification: SidebarOpsNotificationLike,
  poActiveMap: Map<string, boolean>
): boolean {
  if (isSidebarOpsPoIndependentType(notification.type)) {
    return true;
  }

  const poNumber = notification.poNumber ? String(notification.poNumber).trim() : '';
  if (!poNumber) return true;

  const active = poActiveMap.get(poNumber);
  return active === true;
}
